import Box from '@mui/material/Box';
import MuiSwitch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId } from 'react';

import { cssVar } from '../../theme/tokens';
import { mergeSx } from './sizes';

export interface ToggleRowProps {
  label: ReactNode;
  /** One line under the label, announced as the switch's description. */
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** The switch before the text (settings lists) or after it (default, cards). */
  switchPosition?: 'start' | 'end';
  /** Beside the label, for example a Static badge. */
  badge?: ReactNode;
  sx?: SxProps<Theme>;
}

/** A setting with a label, an optional description and a switch; clicking the label toggles it. */
export function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  switchPosition = 'end',
  badge,
  sx,
}: ToggleRowProps) {
  const id = useId();
  const inputId = `${id}-input`;
  const descriptionId = description ? `${id}-description` : undefined;
  const control = (
    <MuiSwitch
      checked={checked}
      disabled={disabled}
      onChange={(_event, next) => onChange(next)}
      slotProps={{
        input: { id: inputId, 'aria-describedby': descriptionId },
      }}
      sx={{ flex: 'none' }}
    />
  );
  return (
    <Stack
      direction="row"
      sx={mergeSx(
        {
          alignItems: description ? 'flex-start' : 'center',
          gap: 3,
          opacity: disabled ? 0.6 : 1,
        },
        sx,
      )}
    >
      {switchPosition === 'start' ? control : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography
            variant="bodySm"
            component="label"
            htmlFor={inputId}
            sx={{ fontWeight: 700, cursor: disabled ? 'default' : 'pointer' }}
          >
            {label}
          </Typography>
          {badge}
        </Stack>
        {description ? (
          <Typography
            id={descriptionId}
            variant="caption"
            component="p"
            sx={{ mt: 0.5, color: cssVar('text-subtle') }}
          >
            {description}
          </Typography>
        ) : null}
      </Box>
      {switchPosition === 'end' ? control : null}
    </Stack>
  );
}
